// src/pages/admin/tabs/properties/CommercialRentPropertiesListPage.jsx
import React from 'react';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import { Card, Row2, Field, Actions, Btn, Badge, TableWrap, Table, Paginator, PageBtn } from '../../ui';
import { useDebounced, clamp } from '../../ui';
import { Link, useNavigate } from 'react-router-dom';

export default function CommercialRentPropertiesListPage(){
  const [regions, setRegions] = React.useState([]);
  const [districts, setDistricts] = React.useState([]);

  const [q, setQ] = React.useState('');
  const dq = useDebounced(q, 300);
  const [regionId, setRegionId] = React.useState('');
  const [districtId, setDistrictId] = React.useState('');
  const [investmentFilter, setInvestmentFilter] = React.useState('');
  const [featuredFilter, setFeaturedFilter] = React.useState('');
  const [sort, setSort] = React.useState('newest');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const totalPages = Math.max(1, Math.ceil((total || items.length) / pageSize));
  const nav = useNavigate();

  React.useEffect(() => {
    (async ()=>{
      try {
        const [rResp, dResp] = await Promise.all([
          PublicAPI.regions(),
          PublicAPI.districts(),
        ]);
        setRegions(rResp?.regions || []);
        setDistricts(dResp?.districts || []);
      } catch (e) {
        console.error('Failed to load regions/districts', e);
        setRegions([]); setDistricts([]);
      }
    })();
  }, []);

  const load = React.useCallback(async ()=>{
    setLoading(true); setMsg('');
    try{
      const params = {
        q: dq || undefined,
        region_id: regionId || undefined,
        district_id: districtId || undefined,
        sort,
        page,
        page_size: pageSize,
        locale: 'ru',
        currency: 'AED',
      };
      
      if (investmentFilter === 'yes') params.is_investment = 'true';
      if (investmentFilter === 'no') params.is_investment = 'false';
      if (featuredFilter === 'yes') params.is_featured = 'true';
      if (featuredFilter === 'no') params.is_featured = 'false';
      
      const data = await AdminAPI.listCommercialRentProperties(params);
      const list = Array.isArray(data) ? data
        : (data.properties || data.items || []);
      const total  = Array.isArray(data) ? list.length
        : (data.pagination?.total ?? data.total ?? list.length);
      setItems(list);
      setTotal(total);
    }catch(e){ setMsg(e.message || 'Не удалось загрузить список'); }
    finally{ setLoading(false); }
  }, [dq, regionId, districtId, investmentFilter, featuredFilter, sort, page, pageSize]);

  React.useEffect(()=>{ setPage(1); }, [dq, regionId, districtId, investmentFilter, featuredFilter, pageSize, sort]);
  React.useEffect(()=>{ load(); }, [load]);

  const delItem = async (p) => {
    if (!window.confirm(`Удалить коммерческий объект "${p.title || p.slug || p.id}"?`)) return;
    try{ await AdminAPI.deleteCommercialRentProperty(p.id); await load(); }
    catch(e){ alert(e.message || 'Ошибка удаления'); }
  };

  const goPage = (n) => setPage(clamp(n, 1, totalPages));

  const findRegionName = id => {
    const r = regions.find(x=>String(x.id)===String(id));
    return r?.name || r?.title || r?.slug || '';
  };
  const findDistrictName = id => {
    const d = districts.find(x=>String(x.id)===String(id));
    return d?.name || d?.title || d?.slug || '';
  };

  // РЕКОМЕНДУЕМЫЕ (featured)
  const [busyFeatured, setBusyFeatured] = React.useState(false);
  const toggleFeatured = async (propertyId, enabled) => {
    if (!propertyId) return;
    try{
      setBusyFeatured(true);
      await AdminAPI.setFeaturedCommercialRentProperty(propertyId, enabled);
      setItems(prev => prev.map(it => it.id === propertyId ? { ...it, is_featured: !!enabled } : it));
      setMsg(enabled ? 'Отмечен как рекомендованный' : 'Снят флаг рекоменд.');
    }catch(e){
      const errorMsg = e?.message || e?.toString() || 'Не удалось обновить флаг рекоменд.';
      alert(errorMsg);
      console.error('Ошибка toggleFeatured:', e);
    }finally{
      setBusyFeatured(false);
    }
  };

  // ИНВЕСТИЦИОННЫЕ (investment)
  const [busyInvestment, setBusyInvestment] = React.useState(false);
  const toggleInvestment = async (propertyId, enabled) => {
    if (!propertyId) return;
    try{
      setBusyInvestment(true);
      await AdminAPI.setInvestmentCommercialRentProperty(propertyId, enabled);
      setItems(prev => prev.map(it => it.id === propertyId ? { ...it, is_investment: !!enabled } : it));
      setMsg(enabled ? 'Отмечен как инвестиционный' : 'Снят флаг инвест.');
    }catch(e){
      const errorMsg = e?.message || e?.toString() || 'Не удалось обновить флаг инвест.';
      alert(errorMsg);
      console.error('Ошибка toggleInvestment:', e);
    }finally{
      setBusyInvestment(false);
    }
  };

  return (
    <>
      <Actions style={{marginBottom:12}}>
        <Btn $kind="primary" onClick={()=>nav('/admin/commercial-rent/create')}>+ Создать коммерческий объект</Btn>
      </Actions>

      <Card>
        <h3>Фильтры и поиск коммерческой аренды</h3>
        {msg && <div style={{color:'#7a7a7a', marginBottom:8}}>{msg}</div>}

        <Field>Поиск
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Название / slug / адрес…" />
        </Field>

        <Row2>
          <Field>Регион
            <select value={regionId} onChange={e=>setRegionId(e.target.value)}>
              <option value="">Все</option>
              {regions.map(r=><option key={r.id} value={r.id}>{r.name || r.title || r.slug}</option>)}
            </select>
          </Field>
          <Field>Район
            <select value={districtId} onChange={e=>setDistrictId(e.target.value)}>
              <option value="">Все</option>
              {districts
                .filter(d=>!regionId || String(d.region_id ?? d.region?.id ?? '')===String(regionId))
                .map(d=><option key={d.id} value={d.id}>{d.name || d.title || d.slug}</option>)}
            </select>
          </Field>
        </Row2>

        <Row2>
          <Field>Инвестиционные
            <select value={investmentFilter} onChange={e=>setInvestmentFilter(e.target.value)}>
              <option value="">Все</option>
              <option value="yes">Только инвестиционные</option>
              <option value="no">Только не инвестиционные</option>
            </select>
          </Field>
          <Field>Рекомендуемые
            <select value={featuredFilter} onChange={e=>setFeaturedFilter(e.target.value)}>
              <option value="">Все</option>
              <option value="yes">Только рекомендуемые</option>
              <option value="no">Только не рекомендуемые</option>
            </select>
          </Field>
        </Row2>

        <Row2>
          <Field>Сортировка
            <select value={sort} onChange={e=>setSort(e.target.value)}>
              <option value="newest">Сначала новые</option>
              <option value="price_asc">Аренда ↑</option>
              <option value="price_desc">Аренда ↓</option>
              <option value="area_asc">Площадь ↑</option>
              <option value="area_desc">Площадь ↓</option>
            </select>
          </Field>
          <Field>На странице
            <select value={pageSize} onChange={e=>setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </Field>
        </Row2>

        <Actions>
          <Btn onClick={()=>{ 
            setQ(''); 
            setRegionId(''); 
            setDistrictId(''); 
            setInvestmentFilter(''); 
            setFeaturedFilter(''); 
            setSort('newest'); 
            setPage(1); 
          }}>
            Сбросить
          </Btn>
          <Badge>Найдено: <b>{total || items.length}</b></Badge>
          {dq && <Badge>Поиск: "{dq}"</Badge>}
        </Actions>
      </Card>

      <Card style={{marginTop:12}}>
        <h3>Список коммерческих объектов в аренду</h3>
        <div style={{opacity:.7, margin:'6px 0 8px', fontSize:13}}>Подсказка: чтобы поменять флаг — нажмите на него.</div>
        {loading ? (
          <div>Загрузка…</div>
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Название</th>
                    <th>Slug</th>
                    <th>Адрес</th>
                    <th>Регион / Район</th>
                    <th>Аренда</th>
                    <th>Флаги</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p=>(
                    <tr key={p.id || p.slug}>
                      <td>{p.id ?? '—'}</td>
                      <td style={{maxWidth:280}}>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          <span>{p.title ?? p.title_ru ?? p.title_en ?? '—'}</span>
                        </div>
                      </td>
                      <td>{p.slug ?? '—'}</td>
                      <td>{p.address ?? p.city ?? '—'}</td>
                      <td>
                        {findRegionName(p.region?.id ?? p.region_id)}{p.district || p.district_id ? ' / ' : ''}
                        {findDistrictName(p.district?.id ?? p.district_id)}
                      </td>
                      <td>
                        {(() => {
                          const priceMin = p.price ?? p.price_base;
                          const priceMax = p.price_max;
                          const currency = p.price_currency || 'AED';
                          
                          if (priceMin && priceMax && priceMin !== priceMax) {
                            return `${Number(priceMin).toLocaleString('ru-RU')}–${Number(priceMax).toLocaleString('ru-RU')} ${currency}/мес`;
                          }
                          return priceMin ? `${Number(priceMin).toLocaleString('ru-RU')} ${currency}/мес` : '—';
                        })()}
                      </td>
                      <td style={{whiteSpace:'nowrap', display:'flex', gap:8}}>
                        <button
                          type="button"
                          className="btn"
                          title={p.is_featured ? 'Снять флаг рекоменд.' : 'Отметить рекоменд.'}
                          onClick={()=>toggleFeatured(p.id, !p.is_featured)}
                          disabled={busyFeatured}
                        >{p.is_featured ? 'Реком. ✓' : 'Реком.'}</button>

                        <button
                          type="button"
                          className="btn"
                          title={p.is_investment ? 'Снять флаг инвест.' : 'Отметить инвест.'}
                          onClick={()=>toggleInvestment(p.id, !p.is_investment)}
                          disabled={busyInvestment}
                        >{p.is_investment ? 'Инвест. ✓' : 'Инвест.'}</button>
                      </td>
                      <td style={{whiteSpace:'nowrap'}}>
                        <Link className="btn" to={`/admin/commercial-rent/${p.id}`}>Ред.</Link>{' '}
                        <Btn onClick={()=>delItem(p)}>Удалить</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>

            <Paginator>
              <PageBtn onClick={()=>goPage(1)} disabled={page<=1}>« Первая</PageBtn>
              <PageBtn onClick={()=>goPage(page-1)} disabled={page<=1}>‹ Назад</PageBtn>
              <PageBtn data-active="true">{page} / {totalPages}</PageBtn>
              <PageBtn onClick={()=>goPage(page+1)} disabled={page>=totalPages}>Вперёд ›</PageBtn>
              <PageBtn onClick={()=>goPage(totalPages)} disabled={page>=totalPages}>Последняя »</PageBtn>
            </Paginator>
          </>
        )}
      </Card>
    </>
  );
}